import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Filenames from the June/July 2026 bulk extraction look like:
//   {phone}_{PJ order id}_{category}_{email}_{index}.ext
// Older (pre-bulk) images look like: {PJ order id}_{category}_{email}.ext - phone-less.
function parseCustomerFromFilename(filename) {
  const withPhone = filename.match(/^(\d{7,15})_PJ\d+_[^_]+_(.+)_\d+\.\w+$/i);
  if (withPhone) {
    return { phone: withPhone[1], email: withPhone[2] === 'None' ? null : withPhone[2] };
  }
  const legacy = filename.match(/^PJ\d+_[^_]+_(.+)\.\w+$/i);
  if (legacy && legacy[1].includes('@')) {
    return { phone: null, email: legacy[1] };
  }
  return null;
}

const BUCKET_META = {
  'Marketing & Promotion': { color: '#be185d', icon: '\u{1F4E3}' },
  'Corporate Stationery & Branding': { color: '#1971c2', icon: '\u{1F58B}\u{FE0F}' },
  'Certificates & Recognition': { color: '#c2410c', icon: '\u{1F3C6}' },
  'Invitations & Events': { color: '#9333ea', icon: '\u{1F389}' },
  'Signage & Wayfinding': { color: '#15803d', icon: '\u{1F9ED}' },
  'Transactional & Billing': { color: '#5f3dc4', icon: '\u{1F9FE}' },
  'Advocacy & Awareness': { color: '#b91c1c', icon: '\u{1F4E2}' },
  'Packaging & Labels': { color: '#0c8599', icon: '\u{1F4E6}' },
  'Educational Materials': { color: '#1c64d6', icon: '\u{1F4D6}' },
  'Official Documents': { color: '#495057', icon: '\u{1F4C4}' },
  'Personal & Non-Business': { color: '#7c7f8a', icon: '\u{1F64B}' },
  'Other': { color: '#9298ab', icon: '✳️' },
};

const BUSINESS_ICONS = {
  'Cafes': '☕', 'Schools': '\u{1F393}', 'Offices': '\u{1F3E2}', 'Restaurants': '\u{1F37D}️',
  'Salons': '\u{1F487}', 'Real Estate Agencies': '\u{1F3E0}', 'Hospitals/Clinics': '\u{1F3E5}',
  'Retail Shops': '\u{1F6CD}️', 'Gyms/Fitness': '\u{1F3CB}️', 'Hotels/Hospitality': '\u{1F3E8}',
  'Wedding/Events': '\u{1F48D}', 'NGOs': '\u{1F91D}', 'IT/Tech Companies': '\u{1F4BB}',
  'Manufacturing': '\u{1F3ED}', 'Bookstores': '\u{1F4DA}', 'Personal/Individual (non-business)': '\u{1F464}',
  'Others': '\u{1F3F7}️',
};

function bucketMeta(bucket) {
  return BUCKET_META[bucket] || BUCKET_META['Other'];
}

function Badge({ children, color, bg }) {
  return (
    <span className="badge" style={{ background: bg || `${color}18`, color, border: `1px solid ${color}40` }}>
      {children}
    </span>
  );
}

function confidenceColor(score) {
  if (score >= 90) return 'var(--good)';
  if (score >= 70) return 'var(--warn)';
  return 'var(--bad)';
}

const DELIVERY_META = {
  'Standard Delivery': { icon: '\u{1F69A}', color: '#1971c2' },
  'Express Delivery': { icon: '\u{26A1}', color: '#c2410c' },
  'Store Pickup': { icon: '\u{1F3EC}', color: '#5f3dc4' },
};

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatValue(v) {
  if (v == null) return null;
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

const CUSTOMER_STATUS_META = {
  'New Customer': { color: '#1971c2', icon: '\u{2728}' },
  'Frequent Buyer': { color: '#15803d', icon: '\u{1F501}' },
  'Returned Customer': { color: '#c2410c', icon: '\u{1F504}' },
};

// customer_status is DB-verified (via the order DB join, across the customer's
// full order history) - only shown when that join succeeded for this order.
function getCustomerStatus(order) {
  if (!order.customer_status) return null;
  return { label: order.customer_status, ...(CUSTOMER_STATUS_META[order.customer_status] || {}) };
}

function imageUrl(order, filename) {
  return `${API_BASE}/images/${encodeURIComponent(order.category)}/${encodeURIComponent(filename)}`;
}

function MultiSelectFilter({ label, selected, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const total = options.reduce((s, o) => s + o.count, 0);
  const toggle = (v) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const summary = selected.length === 0
    ? `All (${total})`
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="uca-field uca-multiselect" ref={ref}>
      <label>{label}</label>
      <button
        type="button"
        className={`uca-multiselect-trigger${selected.length > 0 ? ' active' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{summary}</span>
        <span className={`uca-multiselect-chevron${open ? ' open' : ''}`}>▾</span>
      </button>

      {open && (
        <div className="uca-multiselect-panel">
          <div className="uca-multiselect-actions">
            <button type="button" onClick={() => onChange(options.map((o) => o.value))}>Select all</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </div>
          {options.map(({ value: v, count }) => (
            <label key={v} className="uca-multiselect-option">
              <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />
              <span>{v}</span>
              <span className="uca-multiselect-count">{count}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function ClassificationChips({ order }) {
  const bucket = order.use_case_bucket || 'Unclassified';
  const business = order.business_category || 'Unclassified';
  const meta = bucketMeta(order.use_case_bucket);
  const bizIcon = BUSINESS_ICONS[order.business_category] || '\u{1F3F7}️';

  return (
    <div className="uca-classification-row">
      <div className="uca-classification-item">
        <span className="uca-classification-label">Use Case Bucket</span>
        <Badge color={meta.color}>{meta.icon} {bucket}</Badge>
      </div>
      <div className="uca-classification-item">
        <span className="uca-classification-label">Business Category</span>
        <Badge color="var(--accent)">{bizIcon} {business}</Badge>
      </div>
    </div>
  );
}

function Lightbox({ order, index, onClose, onNavigate }) {
  useEffect(() => {
    if (!order) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate((i) => (i + 1) % order.image_filenames.length);
      if (e.key === 'ArrowLeft') onNavigate((i) => (i - 1 + order.image_filenames.length) % order.image_filenames.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [order, onClose, onNavigate]);

  if (!order) return null;
  const filenames = order.image_filenames;
  const total = filenames.length;
  const current = filenames[index];

  return (
    <div className="uca-lightbox-backdrop" onClick={onClose}>
      <button className="uca-modal-close uca-lightbox-close" onClick={onClose}>&times;</button>

      <div className="uca-lightbox-body" onClick={(e) => e.stopPropagation()}>
        <div className="uca-lightbox-stage">
          {total > 1 && (
            <button
              className="uca-lightbox-nav uca-lightbox-nav-prev"
              onClick={() => onNavigate((i) => (i - 1 + total) % total)}
              aria-label="Previous image"
            >
              &#8249;
            </button>
          )}

          <img
            key={current}
            src={imageUrl(order, current)}
            alt={current}
            className="uca-lightbox-image"
          />

          {total > 1 && (
            <button
              className="uca-lightbox-nav uca-lightbox-nav-next"
              onClick={() => onNavigate((i) => (i + 1) % total)}
              aria-label="Next image"
            >
              &#8250;
            </button>
          )}
        </div>

        <div className="uca-lightbox-caption">
          <span>{order.order_id} &middot; {order.category}</span>
          {total > 1 && <span>{index + 1} / {total}</span>}
        </div>

        {total > 1 && (
          <div className="uca-lightbox-strip">
            {filenames.map((fn, i) => (
              <img
                key={fn}
                src={imageUrl(order, fn)}
                alt={fn}
                className={`uca-lightbox-thumb${i === index ? ' active' : ''}`}
                onClick={() => onNavigate(() => i)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderDetailModal({ order, onClose, onOpenLightbox }) {
  if (!order) return null;
  const customers = order.image_filenames.map(parseCustomerFromFilename).filter(Boolean);
  const customer = customers[0]; // same order = same customer
  const delivery = DELIVERY_META[order.delivery_type];
  const placedDate = formatDate(order.order_placed_on);
  const status = getCustomerStatus(order);

  return (
    <div className="uca-modal-backdrop" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card uca-modal">
        <button className="uca-modal-close" onClick={onClose}>&times;</button>

        <div className="uca-modal-title">{order.order_id}</div>
        <div className="uca-modal-subtitle">
          {order.category} &middot; {order.num_images} image{order.num_images !== 1 ? 's' : ''}
          {order.ordered_quantity != null && <> &middot; Qty {order.ordered_quantity}</>}
          {order.order_value_ex_gst != null && <> &middot; {formatValue(order.order_value_ex_gst)} (ex. GST)</>}
        </div>

        <div className="uca-modal-section">
          <div className="uca-modal-section-label">Classification</div>
          <ClassificationChips order={order} />
        </div>

        <div className="uca-modal-section">
          <div className="uca-modal-section-label">Use Case Description</div>
          <div className="uca-modal-section-body">{order.primary_use_case}</div>
        </div>

        <div className="uca-modal-section" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Badge color="var(--accent)">{order.customer_type}</Badge>
          <Badge color={confidenceColor(order.confidence_score)}>{order.confidence_score}% confidence</Badge>
          {delivery && <Badge color={delivery.color}>{delivery.icon} {order.delivery_type}</Badge>}
          {placedDate && <Badge color="var(--text-muted)" bg="var(--bg-chip)">&#128197; {placedDate}</Badge>}
          {status && <Badge color={status.color}>{status.icon} {status.label}</Badge>}
        </div>

        {customer && (
          <div className="uca-modal-section">
            <div className="uca-modal-section-label">Customer</div>
            <div className="uca-modal-section-body">{customer.phone}{customer.email ? ` · ${customer.email}` : ''}</div>
          </div>
        )}

        {order.printo_existing_products && order.printo_existing_products.length > 0 && (
          <div className="uca-modal-section">
            <div className="uca-modal-section-label">Recommended Products</div>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
              {order.printo_existing_products.map((p) => <Badge key={p} color="#1c64d6">{p}</Badge>)}
            </div>
          </div>
        )}

        <div className="uca-modal-section">
          <div className="uca-modal-section-label">Images ({order.image_filenames.length})</div>
          <div className="uca-image-grid">
            {order.image_filenames.map((fn, i) => (
              <img
                key={fn}
                src={imageUrl(order, fn)}
                alt={fn}
                loading="lazy"
                onClick={() => onOpenLightbox(order, i)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function countBy(orders, key, fallback) {
  const counts = {};
  orders.forEach((o) => {
    const v = o[key] || fallback;
    counts[v] = (counts[v] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count);
}

function TopUseCasesPanel({ options, total }) {
  const top5 = options.slice(0, 5);
  return (
    <div className="card uca-top-panel">
      <div className="uca-panel-title">Top 5 Use Cases</div>
      <div className="uca-top-list">
        {top5.map(({ value, count }) => {
          const pct = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
          const meta = bucketMeta(value);
          return (
            <div key={value} className="uca-top-row">
              <div className="uca-top-row-label">
                <span>{meta.icon} {value}</span>
                <span className="uca-top-row-stats">{count} order{count !== 1 ? 's' : ''} &middot; {pct}%</span>
              </div>
              <div className="uca-top-bar-track">
                <div className="uca-top-bar-fill" style={{ width: `${pct}%`, background: meta.color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function buildInsights(orders, useCaseBucketOptions, businessCategoryOptions, avgConfidence) {
  if (orders.length === 0) return [];
  const total = orders.length;
  const insights = [];

  const topBucket = useCaseBucketOptions[0];
  if (topBucket) {
    const pct = Math.round((topBucket.count / total) * 100);
    insights.push(
      `${bucketMeta(topBucket.value).icon} "${topBucket.value}" is the leading use case at ${pct}% of orders (${topBucket.count}/${total}) — build a dedicated product bundle or promotion around this segment.`
    );
  }

  const b2bCount = orders.filter((o) => o.customer_type === 'B2B').length;
  const b2cCount = total - b2bCount;
  const b2bPct = Math.round((b2bCount / total) * 100);
  if (b2bCount > b2cCount) {
    insights.push(
      `\u{1F3E2} B2B orders dominate at ${b2bPct}% — prioritize bulk-pricing tiers and dedicated account management to retain and grow corporate accounts.`
    );
  } else {
    insights.push(
      `\u{1F464} B2C orders dominate at ${100 - b2bPct}% — invest in consumer-facing personalization to drive repeat retail volume.`
    );
  }

  const topBiz = businessCategoryOptions[0];
  const lowConfidenceCount = orders.filter((o) => (o.confidence_score || 0) < 70).length;
  if (topBiz) {
    const pct = Math.round((topBiz.count / total) * 100);
    insights.push(
      `${BUSINESS_ICONS[topBiz.value] || ''} "${topBiz.value}" is the top business segment at ${pct}% of orders — invest in category-specific templates and targeted outreach to grow it further.`
    );
  }
  if (lowConfidenceCount > 0) {
    insights.push(
      `\u{26A0}\u{FE0F} ${lowConfidenceCount} order${lowConfidenceCount !== 1 ? 's' : ''} scored below 70% classification confidence — flag for manual review.`
    );
  }

  return insights.slice(0, 3);
}

function InsightsPanel({ insights }) {
  if (insights.length === 0) return null;
  return (
    <div className="card uca-insights-panel">
      <div className="uca-panel-title">Actionable Insights for Leadership</div>
      <ul className="uca-insights-list">
        {insights.map((text, i) => <li key={i}>{text}</li>)}
      </ul>
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="uca-pagination">
      <button className="btn btn-secondary" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        &larr; Prev
      </button>
      <span className="uca-pagination-status">Page {page} of {totalPages}</span>
      <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        Next &rarr;
      </button>
    </div>
  );
}

const PAGE_SIZE = 24;

function UseCaseAnalysis() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { order, index }
  const [page, setPage] = useState(1);

  const openLightbox = (order, index = 0) => setLightbox({ order, index });
  const navigateLightbox = (updater) => {
    setLightbox((lb) => (lb ? { ...lb, index: updater(lb.index) } : lb));
  };

  const [businessCategoryFilter, setBusinessCategoryFilter] = useState([]);
  const [productCategoryFilter, setProductCategoryFilter] = useState([]);
  const [useCaseBucketFilter, setUseCaseBucketFilter] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/results/latest`, { withCredentials: true })
      .then((res) => {
        setOrders(res.data.orders || []);
        setLoading(false);
      })
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          setOrders([]);
        } else {
          setError('Failed to load results: ' + err.message);
        }
        setLoading(false);
      });
  }, []);

  const businessCategoryOptions = useMemo(() => countBy(orders, 'business_category', 'Others'), [orders]);
  const productCategoryOptions = useMemo(() => countBy(orders, 'category', 'Uncategorized'), [orders]);
  const useCaseBucketOptions = useMemo(() => countBy(orders, 'use_case_bucket', 'Other'), [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (businessCategoryFilter.length && !businessCategoryFilter.includes(o.business_category || 'Others')) return false;
      if (productCategoryFilter.length && !productCategoryFilter.includes(o.category || 'Uncategorized')) return false;
      if (useCaseBucketFilter.length && !useCaseBucketFilter.includes(o.use_case_bucket || 'Other')) return false;
      if (search) {
        const s = search.toLowerCase();
        const haystack = `${o.order_id} ${o.primary_use_case} ${o.category}`.toLowerCase();
        if (!haystack.includes(s)) return false;
      }
      return true;
    });
  }, [orders, businessCategoryFilter, productCategoryFilter, useCaseBucketFilter, search]);

  useEffect(() => {
    setPage(1);
  }, [businessCategoryFilter, productCategoryFilter, useCaseBucketFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedOrders = useMemo(
    () => filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredOrders, currentPage]
  );

  const avgConfidence = useMemo(() => {
    if (orders.length === 0) return 0;
    return Math.round(orders.reduce((s, o) => s + (o.confidence_score || 0), 0) / orders.length);
  }, [orders]);

  const insights = useMemo(
    () => buildInsights(orders, useCaseBucketOptions, businessCategoryOptions, avgConfidence),
    [orders, useCaseBucketOptions, businessCategoryOptions, avgConfidence]
  );

  if (loading) {
    return (
      <div className="fade-in" style={{ textAlign: 'center', padding: '4rem' }}>
        <span className="loader"></span>
        <p style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>Loading use case analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fade-in card uca-empty" style={{ color: 'var(--bad)' }}>
        {error}
      </div>
    );
  }

  return (
    <div className="fade-in">
      <div className="uca-page-head">
        <div>
          <div className="uca-title">Use Case Analysis</div>
          <div className="uca-subtitle">
            {filteredOrders.length === orders.length
              ? `${orders.length} orders`
              : `${filteredOrders.length} of ${orders.length} orders match filters`}
            {filteredOrders.length > 0 && (
              <> &middot; showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filteredOrders.length)}</>
            )}
          </div>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="card uca-empty">No analysis results yet.</div>
      ) : (
        <>
          <div className="uca-kpis">
            <div className="uca-kpi">
              <div className="uca-kpi-label">Total Orders</div>
              <div className="uca-kpi-value">{orders.length}</div>
            </div>
            <div className="uca-kpi">
              <div className="uca-kpi-label">Avg. Confidence</div>
              <div className="uca-kpi-value" style={{ color: confidenceColor(avgConfidence) }}>{avgConfidence}%</div>
            </div>
            <div className="uca-kpi">
              <div className="uca-kpi-label">Top Use Case Bucket</div>
              <div className="uca-kpi-value small">
                {bucketMeta(useCaseBucketOptions[0]?.value).icon} {useCaseBucketOptions[0]?.value || '—'}
              </div>
            </div>
            <div className="uca-kpi">
              <div className="uca-kpi-label">Top Business Category</div>
              <div className="uca-kpi-value small">
                {BUSINESS_ICONS[businessCategoryOptions[0]?.value] || ''} {businessCategoryOptions[0]?.value || '—'}
              </div>
            </div>
          </div>

          <div className="uca-panels-row">
            <TopUseCasesPanel options={useCaseBucketOptions} total={orders.length} />
            <InsightsPanel insights={insights} />
          </div>

          <div className="card uca-filters">
            <div className="uca-filters-row">
              <MultiSelectFilter
                label="Business Category"
                selected={businessCategoryFilter}
                onChange={setBusinessCategoryFilter}
                options={businessCategoryOptions}
              />
              <MultiSelectFilter
                label="Product Category"
                selected={productCategoryFilter}
                onChange={setProductCategoryFilter}
                options={productCategoryOptions}
              />
              <MultiSelectFilter
                label="Use Case Bucket"
                selected={useCaseBucketFilter}
                onChange={setUseCaseBucketFilter}
                options={useCaseBucketOptions}
              />
              <div className="uca-field">
                <label>Search Order / Use Case</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. PJ6293220 or 'cafe'"
                />
              </div>
              {(businessCategoryFilter.length > 0 || productCategoryFilter.length > 0 || useCaseBucketFilter.length > 0 || search) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => { setBusinessCategoryFilter([]); setProductCategoryFilter([]); setUseCaseBucketFilter([]); setSearch(''); }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <div className="uca-status-note">
            <strong>Customer status logic:</strong> New Customer = no prior order on record &middot;{' '}
            Frequent Buyer = last order was within 90 days &middot; Returned Customer = last order was 90+ days ago.
            Based on each customer's full Printo order history, not just this dataset.
          </div>

          <div className="uca-list">
            {paginatedOrders.map((order) => {
              const customer = parseCustomerFromFilename(order.image_filenames[0]);
              const delivery = DELIVERY_META[order.delivery_type];
              const placedDate = formatDate(order.order_placed_on);
              const status = getCustomerStatus(order);
              return (
                <div
                  key={order.order_id + order.category}
                  className="card uca-order-card"
                  onClick={() => setSelectedOrder(order)}
                >
                  <div
                    className="uca-thumb"
                    onClick={(e) => { e.stopPropagation(); openLightbox(order, 0); }}
                  >
                    <img src={imageUrl(order, order.image_filenames[0])} alt={order.order_id} loading="lazy" />
                    {order.num_images > 1 && (
                      <span className="uca-thumb-count">+{order.num_images - 1}</span>
                    )}
                  </div>

                  <div className="uca-order-body">
                    <div className="uca-order-top">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <span className="uca-order-id">{order.order_id}</span>
                        <span className="uca-order-category">{order.category}</span>
                        {order.ordered_quantity != null && (
                          <span className="uca-order-category">Qty {order.ordered_quantity}</span>
                        )}
                        {order.order_value_ex_gst != null && (
                          <span className="uca-order-category">{formatValue(order.order_value_ex_gst)} (ex. GST)</span>
                        )}
                      </div>
                      <div className="uca-order-meta">
                        <span>{order.num_images} image{order.num_images !== 1 ? 's' : ''}</span>
                        <span className="uca-confidence" style={{ color: confidenceColor(order.confidence_score) }}>
                          <span className="uca-confidence-dot" style={{ background: confidenceColor(order.confidence_score) }}></span>
                          {order.confidence_score}%
                        </span>
                      </div>
                    </div>

                    <ClassificationChips order={order} />

                    {(delivery || placedDate || status) && (
                      <div className="uca-order-tags">
                        {delivery && <Badge color={delivery.color}>{delivery.icon} {order.delivery_type}</Badge>}
                        {placedDate && <Badge color="var(--text-muted)" bg="var(--bg-chip)">&#128197; {placedDate}</Badge>}
                        {status && <Badge color={status.color}>{status.icon} {status.label}</Badge>}
                      </div>
                    )}

                    {customer && (
                      <div className="uca-customer-line">
                        {customer.phone && <span>&#128222; {customer.phone}</span>}
                        {customer.email && <span>&#9993;&#65039; {customer.email}</span>}
                      </div>
                    )}

                    <div className="uca-use-case-text">{order.primary_use_case}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      <OrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onOpenLightbox={openLightbox}
      />
      <Lightbox
        order={lightbox?.order}
        index={lightbox?.index || 0}
        onClose={() => setLightbox(null)}
        onNavigate={navigateLightbox}
      />
    </div>
  );
}

export default UseCaseAnalysis;
